import * as React from "react";
import { useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { CatJobHistoryItem } from "../../../client/types.gen";
import { formatTimestamp } from "../utils";
import { RunTags, hasPolicyTermRun } from "./RunTags";
import styles from "./RunPicker.module.css";

type RunPickerProps = {
  // Completed runs of the model on show, any order.
  jobs: CatJobHistoryItem[];
  // The run the tab's own columns show, resolved by the parent; null when the
  // history has no completed row for it, in which case ``emptyLabel`` shows.
  viewedJob: CatJobHistoryItem | null;
  emptyLabel: string;
  onView: (job: CatJobHistoryItem) => void;
  // Further runs shown as AAL columns beside the viewed one.
  compareBatchIds: string[];
  onCompareChange: (batchIds: string[]) => void;
};

const byNewest = (a: CatJobHistoryItem, b: CatJobHistoryItem): number =>
  new Date(b.started_at).getTime() - new Date(a.started_at).getTime();

// A run as one line: when it started, then what sets it apart.
const RunOption: React.FC<{
  job: CatJobHistoryItem;
  showTermTags: boolean;
}> = ({ job, showTermTags }) => (
  <span className={styles.runOption}>
    <span className={styles.runTime}>{formatTimestamp(job.started_at)}</span>
    <RunTags job={job} showTermTags={showTermTags} />
  </span>
);

/**
 * Which completed run(s) of a model the By Layer and Peril tab shows: one run
 * to view, and optionally more to set beside it.
 */
export const RunPicker: React.FC<RunPickerProps> = ({
  jobs,
  viewedJob,
  emptyLabel,
  onView,
  compareBatchIds,
  onCompareChange,
}) => {
  const sortedJobs = useMemo(() => [...jobs].sort(byNewest), [jobs]);
  const showTermTags = hasPolicyTermRun(jobs);

  const compareOptions = useMemo(
    () => sortedJobs.filter((job) => job.batch_id !== viewedJob?.batch_id),
    [sortedJobs, viewedJob]
  );

  const optionTemplate = (job: CatJobHistoryItem) => (
    <RunOption job={job} showTermTags={showTermTags} />
  );

  return (
    <div className={styles.picker}>
      <span className={styles.label}>Viewing</span>
      <Dropdown
        value={viewedJob?.batch_id ?? null}
        options={sortedJobs}
        optionLabel="started_at"
        optionValue="batch_id"
        itemTemplate={optionTemplate}
        valueTemplate={(job: CatJobHistoryItem | null) =>
          job ? optionTemplate(job) : <span>{emptyLabel}</span>
        }
        onChange={(e) => {
          const job = sortedJobs.find((j) => j.batch_id === e.value);
          if (job) onView(job);
        }}
        className={styles.dropdown}
        panelClassName={styles.panel}
        disabled={sortedJobs.length === 0}
      />
      {sortedJobs.length >= 2 && (
        <>
          <span className={styles.label}>Compare</span>
          <MultiSelect
            value={compareBatchIds}
            options={compareOptions}
            optionLabel="started_at"
            optionValue="batch_id"
            itemTemplate={optionTemplate}
            onChange={(e) => onCompareChange(e.value ?? [])}
            placeholder="Add runs"
            // Always the count: timestamps as chips would swallow the toolbar.
            maxSelectedLabels={0}
            selectedItemsLabel="{0} selected"
            showClear={compareBatchIds.length > 0}
            className={styles.multiselect}
            panelClassName={styles.panel}
          />
        </>
      )}
    </div>
  );
};
